import React, { useEffect, useState, JSX } from 'react'
import { Text } from '@awaze/design-system.components.text'
import { Link } from '@awaze/design-system.components.link'
import { Button, IButtonProps } from '@awaze/design-system.components.button'
import styles from './CountdownBanner.module.scss'

import {
  IButtonOptionsComponentFields,
  ISectionComponentFields,
} from 'contentful/types/contentful-modules'
import { SectionModule } from '@/components/section-composition/section'
import { breakpoints } from '@awaze/design-system.utils.react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

export interface CountdownBannerProps {
  title: string
  subtitle?: string
  textColor: 'dark' | 'light'
  backgroundImage?: string // Contentful Asset url
  backgroundImageMobile?: string // Contentful Asset url
  targetDate: string // Accept target date as a string
  buttonText: IButtonProps['text']
  buttonLink: IButtonProps['href']
  buttonOptions: IButtonOptionsComponentFields
  link?: string
  linkText?: string
  section?: ISectionComponentFields
}

const BannerWrapper = styled.div<{
  backgroundImage
  backgroundImageMobile
}>`
  background: url(${({ backgroundImageMobile }) => backgroundImageMobile}),
    var(--theme-accent-1);
  background-size: cover;
  background-position: center;

  @media (min-width: ${breakpoints[0]}px) {
    background: url(${({ backgroundImage }) => backgroundImage}),
      var(--theme-accent-1);
  }
`

const StyledSectionModule = styled(SectionModule)<{ section }>`
  padding-left: 16px;
  padding-right: 16px;
  padding-top: ${({ section }) => section?.mobilePaddingTop}px;
  padding-bottom: ${({ section }) => section?.mobilePaddingBottom}px;
  background-color: ${({ section }) => section?.backgroundColor};
  @media (min-width: ${breakpoints[0]}px) {
    padding-top: ${({ section }) => section?.desktopPaddingTop}px;
    padding-bottom: ${({ section }) => section?.desktopPaddingBottom}px;
  }
`

export const CountdownBanner: React.FC<CountdownBannerProps> = ({
  title,
  subtitle,
  textColor,
  backgroundImage,
  backgroundImageMobile,
  targetDate,
  buttonText,
  buttonLink,
  buttonOptions,
  link,
  linkText,
  section,
}): JSX.Element => {
  const [timeLeft, setTimeLeft] = useState<number>(0)
  const color =
    textColor === 'dark' ? 'ColorTextBodyOnLight' : 'ColorTextBodyOnDark'

  const { formatMessage } = useIntl()

  useEffect(() => {
    let animationFrameId: number

    // Convert the targetDate string to a Date object
    const targetDateObj = new Date(targetDate)

    const updateCountdown = () => {
      const now = new Date()
      const secondsRemaining = Math.max(
        0,
        Math.floor((targetDateObj.getTime() - now.getTime()) / 1000),
      )

      setTimeLeft(secondsRemaining)

      if (secondsRemaining > 0) {
        animationFrameId = requestAnimationFrame(updateCountdown)
      }
    }

    updateCountdown()

    return () => {
      cancelAnimationFrame(animationFrameId)
    }
  }, [targetDate])

  const days = Math.floor(timeLeft / 86400)
  const hours = Math.floor((timeLeft % 86400) / 3600)
  const minutes = Math.floor((timeLeft % 3600) / 60)
  const seconds = timeLeft % 60

  /** Because the entire module is not localized,
   * once added to the home page, the banner will be displayed
   * only if a date was added. */
  if (targetDate == null) {
    return null
  }
  return (
    <StyledSectionModule handlesLayout section={section}>
      <BannerWrapper
        className={styles.banner}
        backgroundImage={backgroundImage}
        backgroundImageMobile={backgroundImageMobile}
      >
        <div>
          <Text sizes={500} type="bold" color={color}>
            {title}
          </Text>
          <Text sizes={300} color={color}>
            {subtitle}
          </Text>
        </div>

        <div className={styles.countdown}>
          <div className={styles.timeset}>
            <Text sizes={500} type="bold" color={color}>
              {days}
            </Text>
            <Text sizes={100} type="mid" color={color}>
              {formatMessage({
                id: 'countdown-banner.days',
                defaultMessage: 'Days',
              })}
            </Text>
          </div>
          <div className={styles.timeset}>
            <Text sizes={500} type="bold" color={color}>
              {hours}
            </Text>
            <Text sizes={100} type="mid" color={color}>
              {formatMessage({
                id: 'countdown-banner.hours',
                defaultMessage: 'Hours',
              })}
            </Text>
          </div>
          <div className={styles.timeset}>
            <Text sizes={500} type="bold" color={color}>
              {minutes}
            </Text>
            <Text sizes={100} type="mid" color={color}>
              {formatMessage({
                id: 'countdown-banner.minutes',
                defaultMessage: 'Minutes',
              })}
            </Text>
          </div>
          <div className={styles.timeset}>
            <Text sizes={500} type="bold" color={color}>
              {seconds}
            </Text>
            <Text sizes={100} type="mid" color={color}>
              {formatMessage({
                id: 'countdown-banner.seconds',
                defaultMessage: 'Seconds',
              })}
            </Text>
          </div>
        </div>

        <Button
          as={'a'}
          text={buttonText}
          size="small"
          variant={buttonOptions?.variant}
          colourScheme={
            buttonOptions?.colorScheme !== 'none'
              ? buttonOptions?.colorScheme
              : undefined
          }
          href={buttonLink}
          className={styles.button}
        />
      </BannerWrapper>

      {link && (
        <div className={styles.link}>
          <Link href={link} sizes={100}>
            {linkText}
          </Link>
        </div>
      )}
    </StyledSectionModule>
  )
}
